'use client';

import { cn } from '@/lib/ui/cn';
import { BlockListAsideProps } from '@/types/blocks';
import { DirectusFieldBackgroundColor } from '@/types/directus';
import { cva, type VariantProps } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';
import { Button } from '../ui/Button';
import { Show } from '../utils/Show';
import { List } from './List';

const BACKGROUND_COLOR_DEFAULT: DirectusFieldBackgroundColor = 'white';

const sectionVariants = cva('block rounded-4xl px-7 py-10 xl:grid', {
  variants: {
    withImage: {
      true: 'xl:grid-cols-3',
      false: 'xl:grid-cols-2',
    },
    backgroundColor: {
      cream: 'bg-sh-cream',
      white: 'bg-sh-white',
    },
  },
  defaultVariants: {
    withImage: false,
    backgroundColor: BACKGROUND_COLOR_DEFAULT,
  },
});

const desktopColVariants = cva('hidden flex-col px-4 xl:flex @container', {
  variants: {
    withImage: {
      true: '',
      false: 'py-8.5',
    },
  },
  defaultVariants: { withImage: false },
});

const headingDesktop = 'sh-heading-3 mb-3';

const subheadingDesktop = 'sh-body';

// Uses raw Tailwind instead of sh-heading-3 because the mobile design needs
// a 27px base (not 18px) and bumps to 32px at md (not sm), with 100% line
// height at all sizes rather than the 130% that sh-heading-3 applies at sm+.
const headingMobile = cn(
  'xl:hidden font-sans font-medium text-[27px] md:text-[32px]',
  'leading-[100%] tracking-[-0.04em] text-sh-dark-gray mb-2'
);

const subheadingVariantsMobile = cva('xl:hidden', {
  variants: {
    withImage: {
      true: 'sh-body mb-12.5',
      false: 'sh-body md:mb-12.5',
    },
  },
  defaultVariants: { withImage: false },
});

const priceVariants = cva('mb-0 font-sans', {
  variants: {
    viewport: {
      desktop: 'text-[clamp(2rem,17cqi,4.25rem)] font-medium',
      mobile: 'text-[2rem] font-light',
    },
  },
});

const priceDetailVariants = cva(
  'leading-[130%] font-medium tracking-[-0.04em]',
  {
    variants: {
      viewport: {
        desktop: 'text-[clamp(1rem,8cqi,2rem)]',
        mobile: 'text-[27px]',
      },
    },
  }
);

const listInnerVariants = cva('rounded-4xl space-y-5', {
  variants: {
    withImage: {
      true: 'xl:px-7',
      false: 'xl:bg-sh-cyan xl:p-22',
    },
  },
});

type WithImage = VariantProps<typeof sectionVariants>['withImage'];

export function ListAside(props: BlockListAsideProps) {
  const {
    heading,
    subheading,
    backgroundColor,
    button,
    listHeading,
    price,
    priceInterval,
    image,
    items = [],
    promoPrice,
    promoText,
  } = props;
  const withImage: WithImage = !!image;
  const t = useTranslations('ListAside.toggle');

  const [toggle, setToggle] = useState(false);

  const toggleList = () => setToggle(prev => !prev);

  return (
    <section className={sectionVariants({ withImage, backgroundColor })}>
      {/* Desktop text/CTA column */}
      <div className={desktopColVariants({ withImage })}>
        <Show when={heading}>
          <p className={headingDesktop}>{heading}</p>
        </Show>
        <Show when={price}>
          <p className={priceVariants({ viewport: 'desktop' })}>
            <span className='whitespace-nowrap'>
              <span className={`${promoPrice ? 'line-through' : ''}`}>
                {price}&nbsp;
              </span>
              <span className={priceDetailVariants({ viewport: 'desktop' })}>
                /&nbsp;{priceInterval}
              </span>
            </span>
          </p>
        </Show>
        <Show when={promoPrice && price}>
          <p
            className={cn(
              priceVariants({ viewport: 'desktop' }),
              'flex flex-wrap items-baseline gap-x-4'
            )}
          >
            {promoPrice}
            <span
              className={cn(
                priceDetailVariants({ viewport: 'desktop' }),
                'rounded-[20px] bg-[#F7F5EF] px-4 py-1 whitespace-nowrap'
              )}
            >
              {promoText}
            </span>
          </p>
        </Show>
        <Show when={subheading}>
          <p className={subheadingDesktop}>{subheading}</p>
        </Show>
        <Show when={button}>
          <Show when={button?.link}>
            <Button
              variant={button?.theme}
              asChild
              className='mt-12 self-start'
            >
              <Link href={button?.link || ''}>{button?.text}</Link>
            </Button>
          </Show>
          <Show when={!button?.link}>
            <Button variant={button?.theme} className='mt-12 self-start'>
              {button?.text}
            </Button>
          </Show>
        </Show>
      </div>

      {/* Main list column */}
      <List
        toggle={toggle}
        className='p-0! xl:p-0!'
        innerClassName={listInnerVariants({ withImage })}
        beforeContent={
          <>
            <Show when={heading}>
              <p className={headingMobile}>{heading}</p>
            </Show>
            <Show when={price}>
              <p
                className={cn(
                  priceVariants({ viewport: 'mobile' }),
                  'xl:hidden'
                )}
              >
                <span className='whitespace-nowrap'>
                  <span className={`${promoPrice ? 'line-through' : ''}`}>
                    {price}&nbsp;
                  </span>
                  <span className={priceDetailVariants({ viewport: 'mobile' })}>
                    /&nbsp;{priceInterval}
                  </span>
                </span>
              </p>
            </Show>
            <Show when={promoPrice && price}>
              <p
                className={cn(
                  priceVariants({ viewport: 'mobile' }),
                  'flex flex-wrap items-baseline gap-x-4 xl:hidden'
                )}
              >
                {promoPrice}
                <span
                  className={cn(
                    priceDetailVariants({ viewport: 'mobile' }),
                    'rounded-[20px] bg-[#F7F5EF] px-4 py-1 whitespace-nowrap'
                  )}
                >
                  {promoText}
                </span>
              </p>
            </Show>
            <Show when={subheading}>
              <p
                className={cn(
                  toggle ? 'mb-12.5' : 'mb-0',
                  subheadingVariantsMobile({ withImage })
                )}
              >
                {subheading}
              </p>
            </Show>
          </>
        }
        items={items}
        heading={listHeading}
        backgroundColor={backgroundColor}
        afterContent={
          <>
            <Show when={image}>
              <button
                className='md:mt-none text-sh-body block font-bold underline md:hidden'
                onClick={toggleList}
              >
                {toggle ? t('close') : t('open')}
              </button>
            </Show>
            <Show when={button}>
              <Show when={button?.link}>
                <Button
                  variant={button?.theme}
                  asChild
                  className='mt-12.5 mb-12.5 xl:hidden'
                >
                  <Link href={button?.link || ''}>{button?.text}</Link>
                </Button>
              </Show>
              <Show when={!button?.link}>
                <Button
                  variant={button?.theme}
                  className='mt-12.5 mb-12.5 xl:hidden'
                >
                  {button?.text}
                </Button>
              </Show>
            </Show>

            {/* Mobile image inside List */}
            <Show when={image}>
              <div className='relative aspect-square w-full xl:hidden'>
                <Image
                  src={image as string}
                  alt=''
                  priority
                  sizes='(min-width: 1280px) 33vw, 100vw'
                  fill
                  className='rounded-4xl object-cover object-center select-none'
                />
              </div>
            </Show>
          </>
        }
      />
      <Show when={!image}>
        <button
          className='md:mt-none text-sh-body block font-bold underline md:hidden'
          onClick={toggleList}
        >
          {toggle ? t('close') : t('open')}
        </button>
      </Show>
      <Show when={image}>
        <div className='relative hidden w-full xl:block xl:px-2'>
          <div className='relative xl:aspect-[4/5]'>
            <Image
              src={image as string}
              alt=''
              priority
              sizes='(min-width: 1280px) 33vw, 100vw'
              fill
              className='rounded-4xl object-cover object-center select-none'
            />
          </div>
        </div>
      </Show>
    </section>
  );
}
